import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import Spinner from '../common/spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import TableHeader from '../common/table_header';
import routes from '../../routes';
import { getHeaders, handleErrorResponse } from '../../lib/requests';
import PaginationControl from '../common/pagination_control';
import usePaginationAndSorting from '../../hooks/usepaginationandsorting';
import GeneralButton from '../common/button_general';
import GeneralizedTableRow from '../common/table_row';
import { PencilSquareIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import NoResultsFound from '../common/noresultsfound';

function MenuList() {
  const { page, pageSize, sortBy, handlePageChange, handleSortClick } = usePaginationAndSorting('asc_name');
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const location = useLocation();
  const currentRoute = routes.find(route => route.path === location.pathname);
  const routePageTitle = currentRoute ? currentRoute.pageTitle : '';
  const pageTitle = routePageTitle ? routePageTitle : 'Menus';

  useEffect(() => {
    const fetchMenus = async () => {
      if (!pageSize) return;
      setIsLoading(true);
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/menus`, {
          headers: getHeaders(apiToken),
          params: { 
            page, 
            pageSize, 
            ...(sortBy && { order_by: sortBy })
          },
        });

        setTotalPages(response.data.totalPages);
        setTotalRecords(response.data.total);
        setMenus(response.data.data);
      } catch (error) {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenus();
  }, [page, pageSize, sortBy, navigate]);

  const handleDelete = async (menu) => {
    const confirmed = window.confirm(`Are you sure you want to delete menu "${menu.name}"?`);
    if (!confirmed) return;
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      await axios.delete(`${apiUrl}/menus/${menu.menu_id}`, {
        headers: getHeaders(apiToken)
      });
      setMenus(prev => prev.filter(m => m.menu_id !== menu.menu_id));
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const handleDuplicate = async (menu) => {
    const confirmed = window.confirm(`Are you sure you want to duplicate menu "${menu.name}"?`);
    if (!confirmed) return;
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.post(`${apiUrl}/menus/${menu.menu_id}/duplicate`, {}, {
        headers: getHeaders(apiToken)
      });
      const newMenuId = response.data.data;
      navigate(`/menus/${newMenuId}`);
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const columns = [
    { id: 'reference_id', name: 'Reference ID', textColor: 'text-gray-900' },
    { id: 'name', name: 'Menu Name', textColor: 'text-gray-900' },
    { id: 'create_date', name: 'Created Date', textColor: 'text-gray-900' },
    {
      id: 'duplicate',
      name: 'Duplicate',
      tooltip: 'Duplicate Menu',
      render: (rowData) => (
        <button onClick={() => handleDuplicate(rowData)} title="Duplicate Menu">
          <DocumentDuplicateIcon className="h-5 w-5 text-green-500" />
        </button>
      )
    },
    {
      id: 'edit',
      name: 'Edit',
      textColor: 'text-custom-blue',
      link: (value) => `/menus/${value.menu_id}`,
      icon: PencilSquareIcon
    },
    {
      id: 'delete',
      name: '',
      tooltip: 'Delete Menu',
      render: (rowData) => (
        <button onClick={() => handleDelete(rowData)} title="Delete Menu">
          <TrashIcon className="h-5 w-5 text-red-500" />
        </button>
      )
    }
  ];

  return (
    <MasterLayout pageTitle={pageTitle}>
      {showError && <ErrorMessage message={errorMessage} />}
      <div className="px-2 sm:px-3 lg:px-3">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2 flex justify-between">
          <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
          <div className="flex gap-2">
            <GeneralButton type="submit" buttonType="primary" onClick={() => navigate('/menus/create')}>
              Create Menu
            </GeneralButton>
            <GeneralButton type="button" buttonType="primary" onClick={() => navigate('/menus/import')}>
              Import Menu Selections
            </GeneralButton>
          </div>
        </div>
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2">
          <table className="min-w-full divide-y divide-gray-300">
            <TableHeader columns={columns} handleSortClick={handleSortClick} currentSort={sortBy} />
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    <Spinner minHeight={630} />
                  </td>
                </tr>
              ) : menus.length > 0 ? (
                menus.map((menu) => (
                  <GeneralizedTableRow key={menu.menu_id} rowData={menu} columns={columns} />
                ))
              ) : (
                <NoResultsFound message={`No ${pageTitle.toLowerCase()} found`} minHeight={630} />
              )}
            </tbody>
          </table>
          {!isLoading && (
            <PaginationControl
              page={page}
              recordsPerPage={pageSize}
              totalRecords={totalRecords}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </MasterLayout>
  );
}

export default MenuList;
